import React, { useContext, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import AuthContext from '../AuthContext';

const Sidebar = () => {
  const Name = localStorage.getItem('name');
  const currentLocation = useLocation();
  const { permissions, isPlanAllowed, planName, planEndDate } = useContext(AuthContext);

  const redirectPath = permissions.includes('dashboard') ? '/dashboard' : '/emicalculator';

  // A link shows only if user has the permission AND the plan unlocks that page
  const canSee = (key) => permissions.includes(key) && isPlanAllowed(key);
  const [billingOpen,    setBillingOpen]    = useState(false);
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [logoErr,        setLogoErr]        = useState(false);

  const toggleBilling     = () => setBillingOpen(!billingOpen);
  const toggleMarketplace = () => setMarketplaceOpen(!marketplaceOpen);

  // Auto-expand marketplace section when on a marketplace route
  useEffect(() => {
    if (currentLocation.pathname.startsWith('/marketplace')) {
      setMarketplaceOpen(true);
    }
  }, [currentLocation.pathname]);
  const isActive = (path) => currentLocation.pathname === path ? 'active' : '';

  const rawLogo     = localStorage.getItem('company_logo');
  const companyName = localStorage.getItem('companyname') || '';
  // Only build a logo URL when a real filename is stored (not null/undefined literal)
  const logoUrl = rawLogo && rawLogo !== 'null' && rawLogo !== 'undefined'
    ? `https://apiemiride.socialpartners.in/api/company/logo/${encodeURIComponent(rawLogo)}`
    : null;

  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 768) {
      document.body.classList.remove('sidebar-open');
      document.body.classList.add('sidebar-collapse');
    }
  };

  // Letter avatar shown when no logo or logo fails to load — never causes a 404
  const LetterAvatar = () => (
    <span
      className="brand-image img-circle elevation-3 mr-2 d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 33, height: 33, background: '#ffc107', color: '#000', fontWeight: 'bold', fontSize: 15 }}
    >
      {companyName.charAt(0).toUpperCase() || 'E'}
    </span>
  );

  return (
    <aside className="main-sidebar sidebar-dark-primary elevation-4" style={{ minHeight: '100vh' }}>
      {/* Brand Logo */}
      <Link to={redirectPath} className="brand-link d-flex align-items-center" onClick={closeSidebarOnMobile}>
        {!logoErr && logoUrl ? (
          <img
            src={logoUrl}
            alt="Company Logo"
            className="brand-image img-circle elevation-3 mr-2"
            style={{ opacity: '.8', width: 33, height: 33, objectFit: 'cover' }}
            onError={() => setLogoErr(true)}
          />
        ) : (
          <LetterAvatar />
        )}
        <span className="brand-text font-weight-bold text-wrap">
          {companyName || 'EMI Ride'}
        </span>
      </Link>

      <div className="sidebar">
        {/* User Panel */}
        <div className="user-panel mt-3 pb-3 mb-3 d-flex">
          <div className="image">
            <img
              src={`https://apiemiride.socialpartners.in/api/user/profilepicture/${localStorage.getItem('profile_picture')}`}
              className="img-circle elevation-2"
              alt="User"
              style={{ width: '35px', height: '35px', objectFit: 'cover' }}
              onError={(e) => { e.target.onerror = null; e.target.src = '/dist/img/profilepic.jpeg'; }}
            />
          </div>
          <div className="info">
            <span className="d-block text-white font-weight-light">{Name || "User"}</span>
          </div>
        </div>

        {/* Sidebar Menu */}
        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column" data-widget="treeview" role="menu">
            {[ // Menu Items
              { key: "dashboard", icon: "tachometer-alt", label: "Dashboard", path: "/dashboard" },
              { key: "emicalculator", icon: "calculator", label: "EMI Calculator", path: "/emicalculator" },
              { key: "dealer", icon: "store", label: "Dealer", path: "/dealer" },
              { key: "stocks", icon: "chart-bar", label: "Stocks", path: "/stocks" },
              { key: "finance", icon: "hand-holding-usd", label: "Finance", path: "/finance" },
              { key: "exchange", icon: "sync-alt", label: "Exchange", path: "/exchange" },
              { key: "seizedvehicle", icon: "car-crash", label: "Seized Vehicle", path: "/seizedvehicle" },
              { key: "user", icon: "users", label: "User", path: "/user" },
              { key: "collection", icon: "handshake", label: "Collection", path: "/collection" },
              { key: "bulk_upload", icon: "cloud-upload-alt", label: "Bulk Upload", path: "/bulk-upload" }
            ].map(item => (
              canSee(item.key) && (
                <li className="nav-item" key={item.key}>
                  <Link
                    to={item.path}
                    className={`nav-link ${isActive(item.path)}`}
                    onClick={closeSidebarOnMobile}
                  >
                    <i className={`nav-icon fas fa-${item.icon}`} />
                    <p>{item.label}</p>
                  </Link>
                </li>
              )
            ))}

            {/* Billing Dropdown */}
            {(canSee("billing") || canSee("cancelbill") || canSee("search_bill") || canSee("buyer")) && (
              <li className={`nav-item has-treeview ${billingOpen ? 'menu-open' : ''}`}>
                <a href="#" onClick={toggleBilling} className="nav-link">
                  <i className="nav-icon fas fa-file-invoice" />
                  <p>
                    Billing Details
                    <i className="right fas fa-angle-left" />
                  </p>
                </a>
                <ul className="nav nav-treeview">
                  {canSee("billing") && (
                    <li className="nav-item">
                      <Link to="/billing" className={`nav-link ${isActive('/billing')}`} onClick={closeSidebarOnMobile}>
                        <i className="nav-icon fas fa-receipt" />
                        <p>Billing</p>
                      </Link>
                    </li>
                  )}
                  {canSee("search_bill") && (
                    <li className="nav-item">
                      <Link to="/search-bill" className={`nav-link ${isActive('/search-bill')}`} onClick={closeSidebarOnMobile}>
                        <i className="nav-icon fas fa-search-dollar" />
                        <p>Search Bill</p>
                      </Link>
                    </li>
                  )}
                  {canSee("buyer") && (
                    <li className="nav-item">
                      <Link to="/buyer" className={`nav-link ${isActive('/buyer')}`} onClick={closeSidebarOnMobile}>
                        <i className="nav-icon fas fa-user-friends" />
                        <p>Buyer</p>
                      </Link>
                    </li>
                  )}
                  {canSee("cancelbill") && (
                    <li className="nav-item">
                      <Link to="/cancelbill" className={`nav-link ${isActive('/cancelbill')}`} onClick={closeSidebarOnMobile}>
                        <i className="nav-icon fas fa-ban" />
                        <p>Cancel Bill</p>
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}

            {/* Marketplace — always shown for authenticated users */}
            <li className={`nav-item has-treeview ${marketplaceOpen ? 'menu-open' : ''}`}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); toggleMarketplace(); }}
                className={`nav-link ${currentLocation.pathname.startsWith('/marketplace') ? 'active' : ''}`}
              >
                <i className="nav-icon fas fa-globe" />
                <p>
                  Marketplace
                  <i className="right fas fa-angle-left" />
                </p>
              </a>
              <ul className="nav nav-treeview">
                {[
                  { path: '/marketplace',           icon: 'fa-tachometer-alt', label: 'Dashboard'    },
                  { path: '/marketplace/listings',  icon: 'fa-list',           label: 'My Listings'  },
                  { path: '/marketplace/leads',     icon: 'fa-bell',           label: 'Leads'        },
                  { path: '/marketplace/analytics', icon: 'fa-chart-line',     label: 'Analytics'    },
                  { path: '/marketplace/reviews',   icon: 'fa-star',           label: 'Reviews'      },
                ].map((item) => (
                  <li className="nav-item" key={item.path}>
                    <Link
                      to={item.path}
                      className={`nav-link ${isActive(item.path)}`}
                      onClick={closeSidebarOnMobile}
                    >
                      <i className={`nav-icon fas ${item.icon}`} />
                      <p>{item.label}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>

            {/* Reports — plan-gated, no role permission needed */}
            {isPlanAllowed("reports") && (
              <li className="nav-item">
                <Link
                  to="/reports"
                  className={`nav-link ${isActive('/reports')}`}
                  onClick={closeSidebarOnMobile}
                >
                  <i className="nav-icon fas fa-chart-bar" />
                  <p>Reports</p>
                </Link>
              </li>
            )}

            {/* Accounts — plan-gated, no role permission needed */}
            {isPlanAllowed("accounts") && (
              <li className="nav-item">
                <Link
                  to="/accounts"
                  className={`nav-link ${isActive('/accounts')}`}
                  onClick={closeSidebarOnMobile}
                >
                  <i className="nav-icon fas fa-wallet" />
                  <p>Accounts</p>
                </Link>
              </li>
            )}

            {/* Payment History Item */}
            {canSee("paymenthistory") && (
              <li className="nav-item">
                <Link
                  to="/paymenthistory"
                  className={`nav-link ${isActive('/paymenthistory')}`}
                  onClick={closeSidebarOnMobile}
                >
                  <i className="nav-icon fas fa-history" />
                  <p>Payment History</p>
                </Link>
              </li>
            )}

            {/* Plans / current subscription */}
            {canSee("pricing") && (
              <li className="nav-item">
                <Link
                  to="/plans"
                  className={`nav-link ${isActive('/plans')}`}
                  onClick={closeSidebarOnMobile}
                >
                  <i className="nav-icon fas fa-crown" />
                  <p>
                    Plans
                    {planName && (
                      <span
                        className="right badge badge-warning text-dark"
                        style={{ fontSize: '10px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}
                        title={planName}
                      >
                        {planName.replace(/14 Days Free Trial/i, 'Trial').replace(/Professional/i, 'Pro')}
                      </span>
                    )}
                  </p>
                </Link>
                {planEndDate && (
                  <div style={{ paddingLeft: '2.5rem', marginTop: '-6px', marginBottom: '4px' }}>
                    <small className="text-muted" style={{ fontSize: '10px' }}>
                      Valid until {new Date(planEndDate).toLocaleDateString('en-GB')}
                    </small>
                  </div>
                )}
              </li>
            )}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
